import { useState, useEffect } from "react";
import axios from "axios";
import {
  FaBook,
  FaQuestionCircle,

  FaStore,
  FaCog,
  FaSignOutAlt,
  FaPlusCircle,
  FaBookOpen,
  FaBookReader,
  FaCalendarAlt,
  FaUserGraduate,
  FaChartLine,
} from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom"; // Import useNavigate for redirection

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:5000/api";

const QURAN_COURSES = [
  {
    id: 1,
    name: "Qaidah",
    description: "From beginner to advanced-level reading proficiency.",
    icon: <FaBook className="text-green-500" size={24} />,
  },
  {
    id: 2,
    name: "Quran Memorization & Recitation",
    description: "Structured learning for memorizing and perfecting Tajweed.",
    icon: <FaBookOpen className="text-blue-500" size={24} />,
  },
  {
    id: 3,
    name: "Qira'at (Advanced Quranic Recitation)",
    description:
      "Exclusively for Hufaaz who wish to master multiple recitation styles.",
    icon: <FaBookReader className="text-amber-500" size={24} />,
  },
];

const StudentDashboard = () => {
  const navigate = useNavigate(); // Initialize useNavigate
  const [student, setStudent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("All");
  const [courses, setCourses] = useState([]);
  const [showEnrollModal, setShowEnrollModal] = useState(false);
  const [courseStats, setCourseStats] = useState({
    enrolled: 0,
    inProgress: 0,
    finished: 0,
    passed: 0,
    failed: 0,
  });

  useEffect(() => {
    const fetchStudentProfile = async () => {
      try {
        const token = localStorage.getItem("token");

        if (!token) {
          navigate("/login");
          return;
        }

      try {
          const response = await axios.get(`${API_URL}/student/profile`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });

          setStudent(response.data.student);

          // Fetch student's courses
          // In a real implementation, this would come from your API
          // For demonstration, we're simulating a student with one course
          const mockCourses = [
            {
              id: 1,
              courseId: 1, // Qaidah
              name: "Qaidah",
              enrolledDate: "2025-03-15",
              progress: 65,
              grade: null, // Not completed yet
              lastAccessedDate: "2025-04-15",
              resources: [
                {
                  id: 1,
                  title: "Basic Arabic Letters",
                  type: "pdf",
                  url: "/resources/arabic-letters.pdf",
                  completed: true,
                },
                {
                  id: 2,
                  title: "Harakat (Vowel Signs)",
                  type: "pdf",
                  url: "/resources/harakat.pdf",
                  completed: true,
                },
                {
                  id: 3,
                  title: "Reading Practice - Level 1",
                  type: "interactive",
                  url: "/resources/practice-l1",
                  completed: false,
                },
                {
                  id: 4,
                  title: "Reading Practice - Level 2",
                  type: "interactive",
                  url: "/resources/practice-l2",
                  completed: false,
                },
                {
                  id: 5,
                  title: "Final Assessment",
                  type: "quiz",
                  url: "/resources/qaidah-assessment",
                  completed: false,
                },
              ],
            },
          ];

          setCourses(mockCourses);

          // Calculate course statistics
          calculateCourseStats(mockCourses);
        } catch (apiError) {
          // If API request fails due to invalid token, redirect to login
          console.error("API Error:", apiError);
          if (
            apiError.response &&
            (apiError.response.status === 401 ||
              apiError.response.status === 403)
          ) {
            // Clear invalid token
            localStorage.removeItem("token");
            localStorage.removeItem("studentInfo");
            navigate("/login");
            return;
          }
          throw apiError; // Re-throw for general error handling
        }

        setLoading(false);
      } catch (error) {
        setError(error.response?.data?.message || "Failed to fetch profile");
        setLoading(false);
      }
    };

    fetchStudentProfile();
  }, [navigate]);

  // Calculate course statistics based on student's courses and their progress
  const calculateCourseStats = (coursesList) => {
    const stats = {
      enrolled: coursesList.length,
      inProgress: 0,
      finished: 0,
      passed: 0,
      failed: 0,
    };

    coursesList.forEach((course) => {
      // Determine course status based on progress and grade
      const progress = course.progress || 0;
      const grade = course.grade;

      if (progress === 100) {
        stats.finished++;
        if (grade && grade >= 60) {
          stats.passed++;
        } else if (grade && grade < 60) {
          stats.failed++;
        }
      } else if (progress > 0) {
        stats.inProgress++;
      }
    });

    setCourseStats(stats);
  };

  // Handle enrolling in a new course
  const handleEnrollCourse = async (courseId) => {
    try {
      // Check if already enrolled in the course
      if (courses.some((course) => course.courseId === courseId)) {
        toast.info("You are already enrolled in this course.");
        setShowEnrollModal(false);
        return;
      }

      // Check prerequisites for Qira'at course
      if (courseId === 3) {
        const hasCompletedMemorization = courses.some(
          (course) =>
            course.courseId === 2 &&
            course.progress === 100 &&
            course.grade >= 60
        );

        if (!hasCompletedMemorization) {
          toast.error(
            "You must complete the Quran Memorization & Recitation course before enrolling in Qira'at."
          );
          return;
        }
      }

      // In a real implementation, this would be an API call
      // For demonstration, we're simulating the enrollment process
      const selectedCourse = QURAN_COURSES.find((c) => c.id === courseId);

      // Create a new course enrollment
      const newCourse = {
        id: courses.length + 1,
        courseId: courseId,
        name: selectedCourse.name,
        enrolledDate: new Date().toISOString().split("T")[0],
        progress: 0,
        grade: null,
        lastAccessedDate: new Date().toISOString().split("T")[0],
        resources: generateResourcesForCourse(courseId),
      };

      const updatedCourses = [...courses, newCourse];
      setCourses(updatedCourses);

      // Recalculate stats
      calculateCourseStats(updatedCourses);

      // Close the modal and show success
      setShowEnrollModal(false);
      toast.success(`Successfully enrolled in ${selectedCourse.name}!`);
    } catch (error) {
      toast.error(`Failed to enroll in course. Please try again.a ${error}`);
    }
  };

  // Generate resources based on the course
  const generateResourcesForCourse = (courseId) => {
    switch (courseId) {
      case 1: // Qaidah
        return [
          {
            id: 1,
            title: "Basic Arabic Letters",
            type: "pdf",
            url: "/resources/arabic-letters.pdf",
            completed: false,
          },
          {
            id: 2,
            title: "Harakat (Vowel Signs)",
            type: "pdf",
            url: "/resources/harakat.pdf",
            completed: false,
          },
          {
            id: 3,
            title: "Reading Practice - Level 1",
            type: "interactive",
            url: "/resources/practice-l1",
            completed: false,
          },
          {
            id: 4,
            title: "Reading Practice - Level 2",
            type: "interactive",
            url: "/resources/practice-l2",
            completed: false,
          },
          {
            id: 5,
            title: "Final Assessment",
            type: "quiz",
            url: "/resources/qaidah-assessment",
            completed: false,
          },
        ];
      case 2: // Quran Memorization
        return [
          {
            id: 1,
            title: "Introduction to Tajweed",
            type: "pdf",
            url: "/resources/tajweed-intro.pdf",
            completed: false,
          },
          {
            id: 2,
            title: "Juz' Amma Memorization Guide",
            type: "pdf",
            url: "/resources/juz-amma-guide.pdf",
            completed: false,
          },
          {
            id: 3,
            title: "Recitation Practices",
            type: "audio",
            url: "/resources/recitation-samples",
            completed: false,
          },
          {
            id: 4,
            title: "Common Mistakes in Tajweed",
            type: "video",
            url: "/resources/tajweed-mistakes",
            completed: false,
          },
          {
            id: 5,
            title: "Memorization Assessment",
            type: "quiz",
            url: "/resources/memorization-test",
            completed: false,
          },
        ];
      case 3: // Qira'at
        return [
          {
            id: 1,
            title: "Introduction to Qira'at",
            type: "pdf",
            url: "/resources/qiraat-intro.pdf",
            completed: false,
          },
          {
            id: 2,
            title: "The Seven Modes of Recitation",
            type: "pdf",
            url: "/resources/seven-modes.pdf",
            completed: false,
          },
          {
            id: 3,
            title: "Comparative Recitation Samples",
            type: "audio",
            url: "/resources/qiraat-samples",
            completed: false,
          },
          {
            id: 4,
            title: "Advanced Tajweed Rules",
            type: "video",
            url: "/resources/advanced-tajweed",
            completed: false,
          },
          {
            id: 5,
            title: "Qira'at Certification Exam",
            type: "quiz",
            url: "/resources/qiraat-exam",
            completed: false,
          },
        ];
      default:
        return [];
    }
  };

  // Mark a resource as complete
  const markResourceComplete = (courseId, resourceId) => {
    const updatedCourses = courses.map((course) => {
      if (course.courseId === courseId) {
        // Update the resource
        const updatedResources = course.resources.map((resource) => {
          if (resource.id === resourceId) {
            return { ...resource, completed: true };
          }
          return resource;
        });

        // Calculate new progress percentage
        const completedCount = updatedResources.filter(
          (r) => r.completed
        ).length;
        const totalCount = updatedResources.length;
        const newProgress = Math.round((completedCount / totalCount) * 100);

        return {
          ...course,
          resources: updatedResources,
          progress: newProgress,
          lastAccessedDate: new Date().toISOString().split("T")[0],
        };
      }
      return course;
    });

    setCourses(updatedCourses);
    calculateCourseStats(updatedCourses);
    toast.success("Progress updated!");
  };

  // Filter courses based on active tab
  const getFilteredCourses = () => {
    if (activeTab === "All") {
      return courses;
    } else if (activeTab === "In Progress") {
      return courses.filter(
        (course) => (course.progress || 0) > 0 && (course.progress || 0) < 100
      );
    } else if (activeTab === "Finished") {
      return courses.filter((course) => (course.progress || 0) === 100);
    } else if (activeTab === "Passed") {
      return courses.filter(
        (course) => (course.progress || 0) === 100 && (course.grade || 0) >= 60
      );
    } else if (activeTab === "Failed") {
      return courses.filter(
        (course) => (course.progress || 0) === 100 && (course.grade || 0) < 60
      );
    }
    return courses;
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("studentInfo");
    navigate("/login");
  };

  // Check for authentication status upfront - this ensures we don't even render the loading state
  // if there's no token available, we redirect immediately
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/login");
    }
  }, [navigate]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    // If we encounter an error, provide a button to go to login page
    return (
      <div
        className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mx-auto my-8 max-w-2xl"
        role="alert"
      >
        <strong className="font-bold">Error!</strong>
        <span className="block sm:inline"> {error}</span>
        <div className="mt-4">
          <button
            className="bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 px-4 rounded"
            onClick={() => navigate("/login")}
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  // Get filtered courses based on active tab
  const filteredCourses = getFilteredCourses();

  return (
    <div className="flex min-h-screen bg-gray-100">
      <ToastContainer />

      {/* Sidebar */}
      <div className="w-64 bg-white shadow-md">
        {/* Profile Section */}
        <div className="p-4 border-b flex flex-col items-center">
          <div className="w-24 h-24 rounded-full overflow-hidden bg-gray-200 mb-3">
            {student?.profileImage ? (
              <img
                src={`${API_URL.replace("/api", "")}${student.profileImage}`}
                alt={`${student.name}'s profile`}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-gray-200">
                <span className="text-3xl text-gray-400">
                  {student?.name?.charAt(0) || "S"}
                </span>
              </div>
            )}
          </div>
          <h2 className="text-xl font-semibold text-center">
            {student?.name || "Student Name"}
          </h2>
          <p className="text-sm text-gray-500">
            {student?.studentId || "Student ID"}
          </p>
        </div>

        {/* Navigation Menu */}
        <nav className="p-4">
          <ul className="space-y-2">
            <li>
              <a
                href="#"
                className="flex items-center p-3 text-blue-500 bg-blue-50 rounded hover:bg-blue-100"
              >
                <FaBook className="mr-3" />
                <span>My Courses</span>
              </a>
            </li>
            <li>
              <a
                href="#"
                className="flex items-center p-3 text-gray-700 rounded hover:bg-gray-100"
              >
                <FaCalendarAlt className="mr-3" />
                <span>Schedule</span>
              </a>
            </li>
            <li>
              <a
                href="#"
                className="flex items-center p-3 text-gray-700 rounded hover:bg-gray-100"
              >
                <FaUserGraduate className="mr-3" />
                <span>My Teachers</span>
              </a>
            </li>
            <li>
              <a
                href="#"
                className="flex items-center p-3 text-gray-700 rounded hover:bg-gray-100"
              >
                <FaChartLine className="mr-3" />
                <span>Progress Reports</span>
              </a>
            </li>
            <li>
              <a
                href="#"
                className="flex items-center p-3 text-gray-700 rounded hover:bg-gray-100"
              >
                <FaStore className="mr-3" />
                <span>Resource Library</span>
              </a>
            </li>
            <li>
              <a
                href="#"
                className="flex items-center p-3 text-gray-700 rounded hover:bg-gray-100"
              >
                <FaQuestionCircle className="mr-3" />
                <span>Help Center</span>
              </a>
            </li>
            <li>
              <a
                href="#"
                className="flex items-center p-3 text-gray-700 rounded hover:bg-gray-100"
              >
                <FaCog className="mr-3" />
                <span>Settings</span>
              </a>
            </li>
            <li>
              <button
                onClick={handleLogout}
                className="flex items-center p-3 text-red-500 rounded hover:bg-red-50 w-full text-left"
              >
                <FaSignOutAlt className="mr-3" />
                <span>Logout</span>
              </button>
            </li>
          </ul>
        </nav>
      </div>

      {/* Main Content */}
      <div className="flex-1 p-6 overflow-y-auto">
        <h1 className="text-2xl font-bold mb-6">My Courses</h1>

        {/* Stats Row */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-4 flex flex-col items-center justify-center">
            <span className="text-sm text-gray-500">Enrolled</span>
            <span className="text-2xl font-bold">{courseStats.enrolled}</span>
          </div>
          <div className="bg-white rounded-lg shadow p-4 flex flex-col items-center justify-center">
            <span className="text-sm text-gray-500">In Progress</span>
            <span className="text-2xl font-bold text-blue-500">
              {courseStats.inProgress}
            </span>
          </div>
          <div className="bg-white rounded-lg shadow p-4 flex flex-col items-center justify-center">
            <span className="text-sm text-gray-500">Finished</span>
            <span className="text-2xl font-bold text-green-500">
              {courseStats.finished}
            </span>
          </div>
          <div className="bg-white rounded-lg shadow p-4 flex flex-col items-center justify-center">
            <span className="text-sm text-gray-500">Passed</span>
            <span className="text-2xl font-bold text-green-600">
              {courseStats.passed}
            </span>
          </div>
          <div className="bg-white rounded-lg shadow p-4 flex flex-col items-center justify-center">
            <span className="text-sm text-gray-500">Failed</span>
            <span className="text-2xl font-bold text-red-500">
              {courseStats.failed}
            </span>
          </div>
        </div>

        {/* Filter Tabs and Enroll Button */}
        <div className="flex flex-wrap justify-between items-center mb-6">
          <div className="flex space-x-1 mb-4 sm:mb-0 bg-white rounded-lg shadow overflow-hidden">
            {["All", "In Progress", "Finished", "Passed", "Failed"].map(
              (tab) => (
                <button
                  key={tab}
                  className={`px-4 py-2 text-sm ${
                    activeTab === tab
                      ? "bg-blue-500 text-white"
                      : "text-gray-700 hover:bg-gray-100"
                  }`}
                  onClick={() => setActiveTab(tab)}
                >
                  {tab}
                </button>
              )
            )}
          </div>
          <button
            className="bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 px-4 rounded flex items-center"
            onClick={() => setShowEnrollModal(true)}
          >
            <FaPlusCircle className="mr-2" />
            Enroll in New Course
          </button>
        </div>

        {/* Course Cards */}
        {filteredCourses.length === 0 ? (
          <div className="bg-white rounded-lg shadow p-6 text-center">
            <p className="text-gray-500">
              No courses found for the selected filter.
            </p>
            {activeTab !== "All" && (
              <button
                className="mt-4 text-blue-500 hover:underline"
                onClick={() => setActiveTab("All")}
              >
                View all courses
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredCourses.map((course) => (
              <div
                key={course.id}
                className="bg-white rounded-lg shadow overflow-hidden"
              >
                {/* Course Header */}
                <div className="p-4 border-b">
                  <div className="flex items-center">
                    {QURAN_COURSES.find((c) => c.id === course.courseId)
                      ?.icon || <FaBook className="text-gray-500" size={24} />}
                    <h3 className="text-lg font-semibold ml-2">
                      {course.name}
                    </h3>
                  </div>
                  <div className="mt-2 flex justify-between text-sm text-gray-500">
                    <span>Enrolled: {course.enrolledDate}</span>
                    <span>Last accessed: {course.lastAccessedDate}</span>
                  </div>
                </div>

                {/* Progress */}
                <div className="p-4 border-b">
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-sm font-medium">Progress</span>
                    <span className="text-sm font-medium">
                      {course.progress}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full ${
                        course.progress === 100
                          ? course.grade >= 60
                            ? "bg-green-500"
                            : "bg-red-500"
                          : "bg-blue-500"
                      }`}
                      style={{ width: `${course.progress}%` }}
                    ></div>
                  </div>

                  {course.grade !== null && (
                    <div className="mt-2 flex justify-between items-center">
                      <span className="text-sm font-medium">Final Grade</span>
                      <span
                        className={`text-sm font-medium ${
                          course.grade >= 60 ? "text-green-500" : "text-red-500"
                        }`}
                      >
                        {course.grade}%
                      </span>
                    </div>
                  )}
                </div>

                {/* Resources */}
                <div className="p-4">
                  <h4 className="text-sm font-semibold mb-2">
                    Course Resources
                  </h4>
                  <ul className="space-y-2">
                    {course.resources.map((resource) => (
                      <li
                        key={resource.id}
                        className="flex items-center justify-between"
                      >
                        <div className="flex items-center">
                          {resource.type === "pdf" && (
                            <FaBook className="text-red-500 mr-2" size={14} />
                          )}
                          {resource.type === "interactive" && (
                            <FaBookOpen
                              className="text-blue-500 mr-2"
                              size={14}
                            />
                          )}
                          {resource.type === "quiz" && (
                            <FaQuestionCircle
                              className="text-purple-500 mr-2"
                              size={14}
                            />
                          )}
                          {resource.type === "audio" && (
                            <FaBookReader
                              className="text-green-500 mr-2"
                              size={14}
                            />
                          )}
                          {resource.type === "video" && (
                            <FaBookReader
                              className="text-amber-500 mr-2"
                              size={14}
                            />
                          )}
                          <span
                            className={`text-sm ${
                              resource.completed
                                ? "line-through text-gray-400"
                                : ""
                            }`}
                          >
                            {resource.title}
                          </span>
                        </div>
                        {!resource.completed && (
                          <button
                            className="text-xs bg-gray-100 hover:bg-gray-200 text-gray-800 font-semibold py-1 px-2 rounded"
                            onClick={() =>
                              markResourceComplete(course.courseId, resource.id)
                            }
                          >
                            Mark Complete
                          </button>
                        )}
                        {resource.completed && (
                          <span className="text-xs bg-green-100 text-green-800 font-semibold py-1 px-2 rounded">
                            Completed
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Actions */}
                <div className="px-4 py-3 bg-gray-50 flex justify-end">
                  <button className="text-sm text-blue-500 hover:underline mr-4">
                    View Details
                  </button>
                  <button className="text-sm text-blue-500 hover:underline">
                    Continue Learning
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Enroll Modal */}
      {showEnrollModal && (
        <div className="fixed inset-0 bg-[#000000f2] bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-md max-h-screen">
            <div className="px-6 py-3 border-b">
              <h3 className="text-lg font-semibold">Enroll in a New Course</h3>
            </div>
            <div className="p-4">
              <p className="mb-4 text-gray-600">
                Please select a course to enroll in:
              </p>
              <div className="space-y-3">
                {QURAN_COURSES.map((course) => (
                  <div
                    key={course.id}
                    className="border rounded-lg p-4 hover:bg-gray-50 cursor-pointer"
                  >
                    <div className="flex items-center mb-2">
                      {course.icon}
                      <h4 className="text-lg font-semibold ml-2">
                        {course.name}
                      </h4>
                    </div>
                    <p className="text-sm text-gray-600">
                      {course.description}
                    </p>
                    <button
                      className="mt-3 bg-blue-500 hover:bg-blue-600 text-white font-semibold py-1 px-3 rounded text-sm"
                      onClick={() => handleEnrollCourse(course.id)}
                    >
                      Enroll Now
                    </button>
                  </div>
                ))}
              </div>
            </div>
            <div className="px-6 py-1 bg-blue-500 flex justify-end rounded-b-lg">
              <div
                className="text-gray-200 hover:text-gray-800 font-medium "
                onClick={() => setShowEnrollModal(false)}
              >
                Cancel
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentDashboard;
